import type { BookRawJson } from '../lib/bookSchema';
import { safeHttpUrl } from '../lib/safeUrl';

type Props = {
  book: BookRawJson;
};

function safeString(v: unknown): string {
  if (typeof v === 'string') return v;
  return '';
}

function fmtRating(v: unknown): string {
  return typeof v === 'number' ? `${v}/5` : '—';
}

export function BookPreview({ book }: Props) {
  const md = book.metadata;
  const title = safeString(md.title);
  const subtitle = safeString((md as any).subtitle);
  const authors = Array.isArray((md as any).authors) ? ((md as any).authors as unknown[]).filter((x) => typeof x === 'string') : [];
  const coverUrl = safeString((md as any).cover_url);

  const keyTakeaways = Array.isArray((book as any).key_takeaways)
    ? ((book as any).key_takeaways as Array<{ title?: unknown; insight?: unknown }>)
    : [];
  const whoIsThisFor = Array.isArray((book as any).who_is_this_for)
    ? ((book as any).who_is_this_for as unknown[]).filter((x): x is string => typeof x === 'string')
    : [];
  const assessment =
    (book as any).unplay_assessment && typeof (book as any).unplay_assessment === 'object'
      ? ((book as any).unplay_assessment as {
          difficulty_level?: string;
          estimated_impact?: string;
          time_to_value?: string;
          best_listening_context?: string;
          unplay_rating?: {
            content_quality?: number;
            actionability?: number;
            timelessness?: number;
            overall?: number;
          };
        })
      : null;
  const rating = assessment?.unplay_rating ?? null;

  return (
    <div className="card">
      <div className="sectionTitle">Preview</div>

      <div className="kv">
        <div className="k">Title</div>
        <div className="v">{title}</div>
      </div>
      {subtitle ? (
        <div className="kv">
          <div className="k">Subtitle</div>
          <div className="v">{subtitle}</div>
        </div>
      ) : null}
      {authors.length ? (
        <div className="kv">
          <div className="k">Authors</div>
          <div className="v">{authors.join(', ')}</div>
        </div>
      ) : null}
      {safeHttpUrl(coverUrl) ? (
        <div className="kv">
          <div className="k">Cover URL</div>
          <div className="v">
            <a href={safeHttpUrl(coverUrl)!} target="_blank" rel="noreferrer">
              {coverUrl}
            </a>
          </div>
        </div>
      ) : null}

      <div className="sectionTitle">Metadata</div>
      <div className="muted">Preview below reflects only content_json.</div>

      <div className="sectionTitle">FAQ ({book.faq.length})</div>
      <div className="list">
        {book.faq.slice(0, 6).map((x, idx) => (
          <div className="item" key={idx}>
            <div style={{ fontWeight: 750 }}>{x.question}</div>
            <div className="muted" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
              {x.answer}
            </div>
          </div>
        ))}
        {book.faq.length > 6 ? <div className="muted">… {book.faq.length - 6} more</div> : null}
      </div>

      <div className="sectionTitle">Action Plan ({book.action_plan.length})</div>
      <div className="list">
        {book.action_plan.slice(0, 6).map((x, idx) => (
          <div className="item" key={idx}>
            <div style={{ fontWeight: 750 }}>
              Day {x.day}: {x.task_title}
            </div>
            <div className="muted" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
              {x.description}
            </div>
          </div>
        ))}
        {book.action_plan.length > 6 ? <div className="muted">… {book.action_plan.length - 6} more</div> : null}
      </div>

      <div className="sectionTitle">Mind Map</div>
      <div className="item">
        <div className="muted">Label:</div>
        <div style={{ fontWeight: 750, marginTop: 6 }}>{(book.mind_map as any).label ?? '—'}</div>
        <div className="muted" style={{ marginTop: 6 }}>Nodes are shown in the app UI.</div>
      </div>

      <div className="sectionTitle">Glossary ({book.glossary.length})</div>
      <div className="list">
        {book.glossary.slice(0, 8).map((x, idx) => (
          <div className="item" key={idx}>
            <div style={{ fontWeight: 750 }}>{x.term}</div>
            <div className="muted" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
              {x.definition}
            </div>
          </div>
        ))}
        {book.glossary.length > 8 ? <div className="muted">… {book.glossary.length - 8} more</div> : null}
      </div>

      <div className="sectionTitle">Simulation</div>
      <div className="item">
        <div className="muted">Scenario:</div>
        <div style={{ fontWeight: 750, marginTop: 6 }}>{book.simulation.scenario}</div>
        <div className="muted" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
          Challenge: {book.simulation.challenge}
        </div>
        <div className="muted" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
          Solution: {book.simulation.solution}
        </div>
      </div>

      <div className="sectionTitle">Social Snaps ({book.social_snaps.length})</div>
      <div className="list">
        {book.social_snaps.slice(0, 6).map((x, idx) => (
          <div className="item" key={idx}>
            <div style={{ whiteSpace: 'pre-wrap' }}>{x.text}</div>
          </div>
        ))}
        {book.social_snaps.length > 6 ? <div className="muted">… {book.social_snaps.length - 6} more</div> : null}
      </div>

      {keyTakeaways.length ? (
        <>
          <div className="sectionTitle">Key Takeaways ({keyTakeaways.length})</div>
          <div className="list">
            {keyTakeaways.map((x, idx) => (
              <div className="item" key={idx}>
                <div style={{ fontWeight: 750 }}>{safeString(x.title)}</div>
                <div className="muted" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
                  {safeString(x.insight)}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {whoIsThisFor.length ? (
        <>
          <div className="sectionTitle">Who Is This For ({whoIsThisFor.length})</div>
          <div className="list">
            {whoIsThisFor.map((x, idx) => (
              <div className="item" key={idx}>
                <div style={{ whiteSpace: 'pre-wrap' }}>{x}</div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {assessment ? (
        <>
          <div className="sectionTitle">Unplay Assessment</div>
          <div className="item">
            {assessment.difficulty_level ? (
              <div className="kv">
                <div className="k">Difficulty</div>
                <div className="v">{assessment.difficulty_level}</div>
              </div>
            ) : null}
            {assessment.estimated_impact ? (
              <div className="kv">
                <div className="k">Estimated impact</div>
                <div className="v">{assessment.estimated_impact}</div>
              </div>
            ) : null}
            {assessment.best_listening_context ? (
              <div className="kv">
                <div className="k">Best context</div>
                <div className="v">{assessment.best_listening_context}</div>
              </div>
            ) : null}
            {assessment.time_to_value ? (
              <div className="muted" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
                {assessment.time_to_value}
              </div>
            ) : null}
            {rating ? (
              <div className="muted" style={{ marginTop: 8 }}>
                Rating — quality {fmtRating(rating.content_quality)}, actionability{' '}
                {fmtRating(rating.actionability)}, timelessness {fmtRating(rating.timelessness)}, overall{' '}
                {fmtRating(rating.overall)}
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

